import { visibleStations } from '../../domain/library';
import { SORT_MODES, type SortMode } from '../../domain/settings';
import { isHttpOnly, type Station } from '../../domain/stations';
import { type Lang, type TranslationKey, translate } from '../../i18n';
import { clear, el, icon } from '../../lib/dom';
import { closeMenus, initReorder, rewireDs } from '../../lib/ds';
import type { AppState } from '../../store/app';
import {
  fillPicker,
  fillSheet,
  presentSheet,
  type SheetItem,
  syncSheetTrigger,
} from '../components/action-sheet';
import { createSearchField } from '../components/search-field';
import { formatStationDescription, formatStationTech } from '../format';

/**
 * ✅ **Poteg je spet vklopljen od DS v4.21.0** ([D2 §21](../../../docs/04-delivery/D2-ds-requests.md)).
 *
 * Bil je izklopljen od faze 2, ker je `ds/js/overlays.js` klik preklical že
 * pri premiku ≥ 4px in ni gledal smeri. Na dotik je to pomenilo dvoje, oboje
 * izmerjeno na napravi:
 *
 *   • Prst se ob tapu premakne 5–15px → klik se je požrl in postaja se ni
 *     zamenjala. Izmerjeno: 0px in 3px predvajata, 5px, 8px in 12px ne.
 *   • Navpično drsenje z rahlim odklonom je nabiralo vodoravni premik →
 *     vrstica se je odprla sama, sredi drsenja po seznamu.
 *
 * v4.21.0 je prag dvignil na **10px** in dodal **prevladujočo os**: dokler
 * vodoravni odmik ne preseže navpičnega, `overlays.js` kazalca ne zajame in
 * lica ne premakne, zato drsenje ostane mogoče.
 *
 * ⚠️ **Zastavica ostaja**, ker je preklop enovrstičen in ker sta se v tej
 * komponenti zgodili že dve regresiji (D2 §20, §20a). Če se poteg na napravi
 * spet slabo obnese, ga izklopi tu — dejanja so v kebabu, kar je tako ali
 * tako zahtevana pot (poteg je bližnjica, ne edina pot).
 */
const SWIPE_ENABLED = true;

/**
 * Zaslon Postaje — seznam, dotik predvaja, dejanja v vrstici.
 *
 * Vrstica sledi **kanoničnemu markupu DS §19** (v4.20.2), ki rešuje oba
 * konflikta iz zahtevka:
 *
 *   .swipe-row
 *     .swipe-row__acts        uredi · izbriši (poteg)
 *     .ao-row.swipe-row__face
 *       button.ao-row__main   PREDVAJAJ — edini klikabilen del vsebine
 *       .ao-row__acts
 *         button.ao-row__act  ★ priljubljeno (aria-pressed)
 *         .menu-anchor        kebab: gor · dol · uredi · odstrani
 *
 * ⚠️ **`.ao-row` tu ni `<button>`.** Vrstica z dejanji ne sme biti gumb —
 * ugnezdeni interaktivni elementi so neveljaven HTML in bralnik notranjega
 * gumba ne napove. Klikabilen je le `.ao-row__main`; ★ in kebab sta ločena
 * cilja. Tab zaporedje: glavno → ★ → kebab.
 *
 * **Dejanja so dosegljiva tudi brez potega** (pravilo DS pri `.swipe-row`,
 * WCAG 2.5.7): poteg je bližnjica, kebab je pot. Premik gor/dol je v kebabu
 * in ne obstaja samo kot vlečenje.
 *
 * Vrstice se gradijo znova le, kadar se sestava seznama spremeni — sicer bi
 * vsak dotik izgubil fokus in zaprl odprt kebab.
 */

interface Row {
  station: Station;
  face: HTMLDivElement;
  main: HTMLButtonElement;
  action: HTMLSpanElement;
  title: HTMLSpanElement;
  live: HTMLSpanElement;
  liveLabel: HTMLSpanElement;
  sub: HTMLSpanElement;
  subTech: HTMLSpanElement;
  badge: HTMLSpanElement;
  ico: HTMLSpanElement;
  favourite: HTMLButtonElement;
  kebab: HTMLButtonElement;
  menuTop: LabelledButton;
  menuUp: LabelledButton;
  menuDown: LabelledButton;
  menuBottom: LabelledButton;
  menuShare: LabelledButton;
  menuEdit: LabelledButton;
  menuDelete: LabelledButton;
  swipeEdit: LabelledButton;
  swipeDelete: LabelledButton;
  root: HTMLElement;
}

export interface StationsScreenDeps {
  onPlay(stationId: string): void;
  onQuery(query: string): void;
  onSortMode(mode: SortMode): void;
  onFavouritesOnly(on: boolean): void;
  onToggleFavourite(stationId: string): void;
  onMove(stationId: string, step: 1 | -1): void;
  /**
   * Skok na rob seznama (P2 §1.8). Ločen od `onMove`, ker zaslon ne pozna
   * dolžine celotne knjižnice — vidni seznam je lahko filtriran, vrstni red
   * pa je globalen.
   */
  onMoveEdge(stationId: string, edge: 'top' | 'bottom'): void;
  /**
   * Premik na poljubno mesto (način urejanja, vlečenje). Isti vhod kot
   * `reorderStation()` — indeks se omeji v domeni, zaslon dolžine ne pozna.
   */
  onReorder(stationId: string, toIndex: number): void;
  /** Deli postajo (P2 §3.13): Web Share, kjer je, sicer odložišče. */
  onShare(stationId: string): void;
  onEdit(stationId: string): void;
  onDelete(stationId: string): void;
  onAdd(): void;
}

/**
 * Gumb z ikono in besedilom v ločenem `<span>`.
 *
 * Besedilo mora imeti svoje vozlišče: `button.append(niz)` ob vsakem izrisu
 * doda NOV vozel in oznaka bi se z vsakim posodabljanjem podvojila.
 */
interface LabelledButton {
  button: HTMLButtonElement;
  label: HTMLSpanElement;
}

function labelledButton(className: string, iconId: string, size: number): LabelledButton {
  const label = el('span');
  const button = el('button', { class: className, type: 'button' }, [icon(iconId, size), label]);
  return { button, label };
}

function buildRow(station: Station, deps: StationsScreenDeps): Row {
  const ico = el('span', { class: 'r-ico' }, [icon('i-radio', 19)]);

  // Dejanje pove bralnik pred imenom; vidna vsebina ostane cela, ker
  // aria-label bi kodek in bitrate skril.
  const action = el('span', { class: 'nl-sr' });
  const liveLabel = el('span', { class: 'dot-live__label' });
  const live = el('span', { class: 'dot-live', hidden: '' }, [
    el('span', { class: 'dot-live__dot' }),
    liveLabel,
  ]);
  const title = el('span', { class: 'r-name' });
  /**
   * Podnaslov je ENA enota, ki se reže z elipso (DS `.r-sub`), mono del pa
   * je `<span>` znotraj nje. Ločeni razpeti v flex vrstici se tepeta: prva
   * se odreže, druga pa prelomi v novo vrstico in vrstica postaje zraste.
   */
  const subTech = el('span', { class: 'nw-meta' });
  const sub = el('span', { class: 'r-sub' }, [subTech]);
  const badge = el('span', { class: 'badge neutral', hidden: '' });

  const main = el('button', { class: 'ao-row__main', type: 'button' }, [
    ico,
    el('span', { class: 'r-main' }, [
      el('span', { class: 'r-title' }, [action, title, live]),
      el('span', { class: 'r-sub-line' }, [sub, badge]),
    ]),
  ]);
  main.addEventListener('click', () => {
    deps.onPlay(station.id);
  });

  /* ── ★ ────────────────────────────────────────────────────────────
     Preklopni gumb, ne izbira: `aria-pressed`. Pomen nosi POLNILO zvezde
     (DS napolni `[aria-pressed="true"] svg`), ne le barva — prazna proti
     polni je razlika, ki jo vidi tudi barvno slep uporabnik (CLAUDE.md,
     pravilo 6). Ime dejanja je v `aria-label` in se spreminja s stanjem. */
  const favourite = el('button', {
    class: 'ao-row__act',
    type: 'button',
    'aria-pressed': 'false',
  });
  favourite.append(icon('i-star', 20));
  favourite.addEventListener('click', () => {
    deps.onToggleFavourite(station.id);
  });

  /* ── Kebab ────────────────────────────────────────────────────────
     Odpiranje in zapiranje ureja `ds/js/menu.js` prek `[data-menu]`; mi
     samo poslušamo klike na postavke. */
  const kebab = el('button', {
    class: 'ao-row__act',
    type: 'button',
    'data-menu': '',
    'aria-haspopup': 'true',
    'aria-expanded': 'false',
  });
  kebab.append(icon('i-more-vertical', 20));

  /**
   * ⚠️ **»Na vrh« in »na dno« nista okrasje, ampak popravek merila.**
   *
   * Prijava lastnika 2026-07-22: premik za deset mest je deset klikov. Vlečenje
   * bi to rešilo, a DS ima za `.drag-handle` samo sloge in nobenega modula
   * ([D2 §49](../../../docs/04-delivery/D2-ds-requests.md)) — lastna izvedba bi
   * bila komponenta, definirana na strani.
   *
   * Skok na rob pokrije daleč najpogostejši primer (postajo, ki jo poslušaš,
   * hočeš prvo) in ne zahteva ničesar od DS. Vlečenje ju bo dopolnilo, ne
   * nadomestilo: WCAG 2.5.7 zahteva pot z enim kazalcem tudi potem.
   *
   * ⚠️ **Dvojni ševron pomeni skok, puščica korak.** Do DS v4.24.0 je bil tu
   * navaden ševron, ker dvojnega v spritu ni bilo; razliko je nosil samo napis.
   * Postavke so urejene vrh → gor → dol → dno, da je pomen berljiv tudi iz
   * vrstnega reda.
   */
  const menuTop = labelledButton('dd__item', 'i-chevrons-up', 16);
  const menuUp = labelledButton('dd__item', 'i-arrow-up', 16);
  const menuDown = labelledButton('dd__item', 'i-arrow-down', 16);
  const menuBottom = labelledButton('dd__item', 'i-chevrons-down', 16);
  const menuShare = labelledButton('dd__item', 'i-share', 16);
  const menuEdit = labelledButton('dd__item', 'i-edit', 16);
  const menuDelete = labelledButton('dd__item danger', 'i-trash', 16);

  /**
   * Vsaka postavka je KONČNO dejanje, zato meni za sabo zapremo sami —
   * `menu.js` ga ob kliku vase namenoma pusti odprtega (glej `closeMenus`).
   * Brez tega odprt meni visi nad modalom in mu na WebKitu prestreza klike.
   */
  function fromMenu(run: () => void): () => void {
    return () => {
      closeMenus();
      run();
    };
  }

  menuTop.button.addEventListener(
    'click',
    fromMenu(() => {
      deps.onMoveEdge(station.id, 'top');
    }),
  );
  menuUp.button.addEventListener(
    'click',
    fromMenu(() => {
      deps.onMove(station.id, -1);
    }),
  );
  menuDown.button.addEventListener(
    'click',
    fromMenu(() => {
      deps.onMove(station.id, 1);
    }),
  );
  menuBottom.button.addEventListener(
    'click',
    fromMenu(() => {
      deps.onMoveEdge(station.id, 'bottom');
    }),
  );
  menuShare.button.addEventListener(
    'click',
    fromMenu(() => {
      deps.onShare(station.id);
    }),
  );
  menuEdit.button.addEventListener(
    'click',
    fromMenu(() => {
      deps.onEdit(station.id);
    }),
  );
  menuDelete.button.addEventListener(
    'click',
    fromMenu(() => {
      deps.onDelete(station.id);
    }),
  );

  /**
   * ⚠️ **Na dotiku ista dejanja nosi bottom sheet, ne `.dd`.**
   *
   * Postavke se **berejo iz menija**, ne gradijo drugič: oznake prevaja
   * `update()`, omogočenost premika pa izračuna razvrstitev. Drug vir bi se
   * razšel prvič, ko bi se spremenilo eno od dvojega — najverjetneje tiho.
   */
  function sheetItems(): SheetItem[] {
    return [
      {
        icon: 'i-chevrons-up',
        label: menuTop.label.textContent ?? '',
        disabled: menuTop.button.disabled,
        run: () => {
          deps.onMoveEdge(station.id, 'top');
        },
      },
      {
        icon: 'i-arrow-up',
        label: menuUp.label.textContent ?? '',
        disabled: menuUp.button.disabled,
        run: () => {
          deps.onMove(station.id, -1);
        },
      },
      {
        icon: 'i-arrow-down',
        label: menuDown.label.textContent ?? '',
        disabled: menuDown.button.disabled,
        run: () => {
          deps.onMove(station.id, 1);
        },
      },
      {
        icon: 'i-chevrons-down',
        label: menuBottom.label.textContent ?? '',
        disabled: menuBottom.button.disabled,
        run: () => {
          deps.onMoveEdge(station.id, 'bottom');
        },
      },
      {
        icon: 'i-share',
        label: menuShare.label.textContent ?? '',
        ariaLabel: menuShare.button.getAttribute('aria-label') ?? undefined,
        run: () => {
          deps.onShare(station.id);
        },
      },
      {
        icon: 'i-edit',
        label: menuEdit.label.textContent ?? '',
        run: () => {
          deps.onEdit(station.id);
        },
      },
      {
        icon: 'i-trash',
        label: menuDelete.label.textContent ?? '',
        danger: true,
        run: () => {
          deps.onDelete(station.id);
        },
      },
    ];
  }

  syncSheetTrigger(kebab);
  kebab.addEventListener('click', () => {
    // Vrne `false` pri miški — takrat panel odpre `menu.js` in tu ni dela.
    if (!syncSheetTrigger(kebab)) return;
    fillSheet(title.textContent ?? '', sheetItems());
    presentSheet(kebab);
  });

  const menu = el('span', { class: 'menu-anchor' }, [
    kebab,
    el('div', { class: 'dd', role: 'menu' }, [
      menuTop.button,
      menuUp.button,
      menuDown.button,
      menuBottom.button,
      menuShare.button,
      menuEdit.button,
      el('div', { class: 'dd__sep' }),
      menuDelete.button,
    ]),
  ]);

  const face = el('div', { class: SWIPE_ENABLED ? 'ao-row swipe-row__face' : 'ao-row' }, [
    main,
    el('span', { class: 'ao-row__acts' }, [favourite, menu]),
  ]);

  /* ── Poteg ────────────────────────────────────────────────────────
     Dve dejanji, zato `--swipe-open` ostane privzet (2 × `--swipe-act-w`).
     `tabindex="-1"`: ista dejanja so že v kebabu in bi bila v tabulatorju
     podvojena. Fiziko potega vozi `ds/js/overlays.js`. */
  const swipeEdit = labelledButton('edit', 'i-edit', 17);
  const swipeDelete = labelledButton('del', 'i-trash', 17);
  swipeEdit.button.tabIndex = -1;
  swipeDelete.button.tabIndex = -1;
  swipeEdit.button.addEventListener('click', () => {
    deps.onEdit(station.id);
  });
  swipeDelete.button.addEventListener('click', () => {
    deps.onDelete(station.id);
  });

  /**
   * Brez potega gre lice **naravnost v seznam**, brez ovoja.
   *
   * Ovoj bi bil videti nedolžen, a `.ao-list .ao-row:last-child` odstrani
   * spodnjo ločnico — in v ovoju je vsaka vrstica zadnji otrok, torej bi
   * ločnice izginile vse. Enako `.ao-row.swipe-row__face` postavi
   * `border-bottom: 0`, ker ločnico sicer nosi `.swipe-row`.
   */
  const root = SWIPE_ENABLED
    ? el('div', { class: 'swipe-row' }, [
        el('div', { class: 'swipe-row__acts', 'aria-hidden': 'true' }, [
          swipeEdit.button,
          swipeDelete.button,
        ]),
        face,
      ])
    : face;

  return {
    station,
    root,
    face,
    main,
    action,
    title,
    live,
    liveLabel,
    sub,
    subTech,
    badge,
    ico,
    favourite,
    kebab,
    menuTop,
    menuUp,
    menuDown,
    menuBottom,
    menuShare,
    menuEdit,
    menuDelete,
    swipeEdit,
    swipeDelete,
  };
}

/**
 * Vrstica v načinu urejanja — **kanonični markup DS `.reorder__*`** (v4.26.0),
 * ne `.ao-row`.
 *
 * ⚠️ **Zakaj ločen izris in ne priklop na obstoječo vrstico.** `ds/js/reorder.js`
 * trdo išče `.reorder__row` in `.reorder__handle`; `.ao-row` ju nima, njegov
 * CSS pa bi z `.reorder__row` trčil. Način urejanja je zato svoj pogled seznama:
 * brez ★, brez predvajanja ob tapu, brez potega vstran — v urejanju postaj ne
 * poslušaš, jih razvrščaš.
 *
 * `data-station-id` je most do domene: `onReorder` prebere id iz premaknjene
 * vrstice. `data-reorder-label` je ime za naznanilo (modul ga bere sam).
 * Kebab (`data-reorder-top`/`-bottom`) upravlja modul brez naše kode.
 */
function buildReorderRow(station: Station, lang: Lang): HTMLElement {
  const handle = el('button', {
    class: 'reorder__handle',
    type: 'button',
    'aria-label': translate(lang, 'station.dragHandle', { station: station.name }),
  });
  handle.append(icon('i-grip-vertical', 18));

  const idx = el('span', { class: 'reorder__idx', 'aria-hidden': 'true' });
  const name = el('div', { class: 'reorder__n' }, [station.name]);
  const desc = el('div', { class: 'reorder__s' }, [formatStationDescription(lang, station)]);
  const body = el('div', { class: 'reorder__t' }, [name, desc]);

  const kebab = el('button', {
    class: 'reorder__kebab',
    type: 'button',
    'aria-haspopup': 'true',
    'aria-expanded': 'false',
    'aria-label': translate(lang, 'station.more', { station: station.name }),
  });
  kebab.append(icon('i-more-vertical', 20));

  const topBtn = el('button', { type: 'button', role: 'menuitem', 'data-reorder-top': '' }, [
    icon('i-chevrons-up', 16),
    el('span', {}, [translate(lang, 'station.moveTop')]),
  ]);
  const bottomBtn = el('button', { type: 'button', role: 'menuitem', 'data-reorder-bottom': '' }, [
    icon('i-chevrons-down', 16),
    el('span', {}, [translate(lang, 'station.moveBottom')]),
  ]);
  const menu = el('div', { class: 'reorder__menu', role: 'menu' }, [topBtn, bottomBtn]);

  return el(
    'div',
    {
      class: 'reorder__row',
      role: 'listitem',
      'data-station-id': station.id,
      'data-reorder-label': station.name,
    },
    [handle, idx, body, el('div', { class: 'menu-anchor' }, [kebab, menu])],
  );
}

export function createStationsScreen(
  root: HTMLElement,
  deps: StationsScreenDeps,
): { update(state: AppState): void } {
  // `.swipe-list` da robove in ovoj potega, `.ao-list` pravila vrstice.
  const list = el('div', { class: 'ao-list swipe-list nw-stations', role: 'group' });

  /* ── Orodna vrstica ──────────────────────────────────────────────── */
  // `id` in `name` nista za nas: brez njiju brskalnik polja ne zna povezati s
  // svojim samodejnim izpolnjevanjem. Bralnik zaslona je pokrit z `aria-label`
  // spodaj, zato to ni dostopnostna, ampak uporabnostna podrobnost.
  const searchField = createSearchField('stationSearch', deps.onQuery);

  /* ── Razvrstitev: en gumb, ki pokaže način, in meni s štirimi izbirami ──
     Štirje segmenti so jemali celo drugo vrstico orodne vrstice za nekaj, kar
     se nastavi redko. Gumb **mora nositi trenutni način v napisu** — brez tega
     je stanje nevidno, dokler menija ne odpreš. */
  const sortLabel = el('span', { class: 'nw-sort__label' });
  const sortTrigger = el(
    'button',
    {
      class: 'btn secondary nw-sort__trigger',
      type: 'button',
      'data-menu': '',
      'aria-haspopup': 'true',
      'aria-expanded': 'false',
    },
    [icon('i-list-ol', 16), sortLabel, icon('i-chevron-down', 14)],
  );

  const sortItems = SORT_MODES.map((mode) => {
    const label = el('span');
    const check = icon('i-check', 15);
    check.setAttribute('class', 'icon dd__check');
    const button = el(
      'button',
      { class: 'dd__item', type: 'button', role: 'menuitemradio', 'aria-checked': 'false' },
      [label, check],
    );
    button.addEventListener('click', () => {
      closeMenus();
      deps.onSortMode(mode);
    });
    return { mode, button, label, check };
  });

  /* `.dd.start` — poravnava na začetek gumba; flip in shift od DS v4.23.0
     poskrbita, da meni ob robu ne uide z zaslona. */
  const sortMenu = el('span', { class: 'menu-anchor nw-sort' }, [
    sortTrigger,
    el(
      'div',
      { class: 'dd start', role: 'menu' },
      sortItems.map((item) => item.button),
    ),
  ]);

  /** Na dotiku ista izbira pride z dna, brez iskalnega polja (štiri izbire). */
  syncSheetTrigger(sortTrigger);
  sortTrigger.addEventListener('click', () => {
    if (!syncSheetTrigger(sortTrigger)) return;
    fillPicker({
      title: sortTrigger.getAttribute('aria-label') ?? '',
      searchLabel: '',
      searchPlaceholder: '',
      emptyText: '',
      searchable: false,
      value: currentSort,
      options: sortItems.map((item) => ({
        value: item.mode,
        label: item.label.textContent ?? '',
      })),
      onPick: (value) => {
        deps.onSortMode(value as SortMode);
      },
    });
    presentSheet(sortTrigger);
  });

  let currentSort: SortMode = 'custom';

  const favourites = el(
    'button',
    { class: 'chip nw-chip-icon', type: 'button', 'aria-pressed': 'false' },
    [icon('i-star', 17)],
  );
  favourites.addEventListener('click', () => {
    deps.onFavouritesOnly(!favourites.matches('.active'));
  });

  /**
   * Primarnega gumba tu NI — stoji v topbaru (`#addStation`).
   *
   * Pri 390px je orodna vrstica z njim zrasla na tri vrstice in pahnila
   * seznam za ~180px navzdol. V topbaru je dejanje dosegljivo z vsakega
   * mesta v seznamu, tudi ko je uporabnik zdrsel na dno.
   */
  /**
   * »Preuredi« → način urejanja (P2 §1.7). V urejanju postane »Končaj«.
   * Na sledilnem robu ob razvrstitvi; skrit, dokler ni vsaj dveh postaj —
   * enega ni kam premakniti.
   */
  const editToggle = el('button', { class: 'btn secondary nw-edit', type: 'button' });

  const toolbar = el('div', { class: 'nw-toolbar' }, [
    searchField.root,
    favourites,
    sortMenu,
    editToggle,
  ]);

  // Prazno stanje ima naslov, opis in dejanje — nikoli le »ni podatkov« (P4 §6).
  const emptyTitle = el('div', { class: 'ftitle' });
  const emptyBody = el('div', { class: 'desc' });
  const emptyAction = el('button', { class: 'btn primary', type: 'button' });
  emptyAction.addEventListener('click', deps.onAdd);
  const empty = el('div', { class: 'empty', hidden: '' }, [emptyTitle, emptyBody, emptyAction]);

  /**
   * ⚠️ **Ločen izris za urejanje — `.reorder`, ne `.ao-list`.** Živ DOM v lasti
   * `ds/js/reorder.js`: modul ga med urejanjem prestavlja sam. Zgradimo ga le
   * ob **vstopu**; store zapis (`onReorder`) teče v ozadju za persistenco in
   * ga NE prezidava — sicer bi vsak premik vrgel DOM, ki ga je modul pravkar
   * postavil, in z njim fokus na ročici.
   */
  const reorderList = el('div', { class: 'reorder nw-reorder', role: 'list', hidden: '' });
  const reorderHint = el('p', { class: 'nw-note nw-reorder__hint', hidden: '' });

  clear(root);
  root.append(toolbar, reorderHint, list, reorderList, empty);

  let rows: Row[] = [];
  let renderedIds = '';
  let editing = false;
  let wasEditing = false;
  let currentLang: Lang = 'en';

  function rebuild(stations: readonly Station[]): void {
    rows = stations.map((station) => buildRow(station, deps));
    list.replaceChildren(...rows.map((row) => row.root));

    // T5 §5: overlays.js veže poteg ob DOMContentLoaded, torej po vsakem renderju.
    rewireDs();
  }

  /**
   * Zgradi `.reorder` seznam ob VSTOPU v urejanje — vedno iz **custom reda čez
   * cel seznam**, ne glede na trenutni UI sort ali filter (odločeno 2026-07-23):
   * med urejanjem ni ne iskanja ne ★, ker je premik v filtrirani podmnožici
   * dvoumen. Preklop UI sorta na `custom` teče vzporedno (`toggleEdit`), da
   * `.ao-list` ob izstopu kaže isti red.
   */
  function buildReorderList(stations: readonly Station[], lang: Lang): void {
    const ordered = visibleStations(stations, {
      sortMode: 'custom',
      favoritesOnly: false,
      query: '',
      lang,
    });
    reorderList.replaceChildren(
      ...ordered.map((station, index) => {
        const row = buildReorderRow(station, lang);
        const idx = row.querySelector('.reorder__idx');
        if (idx !== null) idx.textContent = String(index + 1);
        return row;
      }),
    );
    initReorder(reorderList, {
      onReorder: (item, _from, to) => {
        const id = (item as HTMLElement).dataset.stationId;
        if (id !== undefined) deps.onReorder(id, to);
      },
      // Naznanilo v jeziku aplikacije (D2 §50). Bere se `currentLang`, ne
      // zajeti jezik ob vstopu — spremenljivka, da sledi menjavi jezika.
      announce: (item, to, total) =>
        translate(currentLang, 'stations.reorder.moved', {
          station: (item as HTMLElement).dataset.reorderLabel ?? '',
          index: String(to + 1),
          total: String(total),
        }),
    });
  }

  function toggleEdit(state: AppState): void {
    editing = !editing;
    // Premik ima smisel le v custom redu; sicer bi ga razvrstitev takoj
    // prekrila. Preklop persistira, da .ao-list ob izstopu kaže isti red.
    if (editing && state.settings.sortMode !== 'custom') deps.onSortMode('custom');
    apply(state);
  }

  let lastState: AppState | null = null;

  function apply(state: AppState): void {
    lastState = state;
    const { lang } = state;
    currentLang = lang;
    list.setAttribute('aria-label', translate(lang, 'a11y.stationList'));

    searchField.update(lang, 'stations.search.label', 'stations.search.placeholder');
    // Vrednost popravimo le, kadar se razideta — sicer bi vsak izris med
    // tipkanjem premaknil kazalec na konec.
    searchField.setValue(state.query);

    currentSort = state.settings.sortMode;
    const sortName = translate(lang, 'stations.sort.label');
    sortTrigger.setAttribute('aria-label', sortName);
    sortMenu.querySelector('.dd')?.setAttribute('aria-label', sortName);
    sortLabel.textContent = translate(lang, `stations.sort.${currentSort}` as TranslationKey);
    for (const { mode, button, label, check } of sortItems) {
      label.textContent = translate(lang, `stations.sort.${mode}` as TranslationKey);
      const active = currentSort === mode;
      button.setAttribute('aria-checked', String(active));
      check.toggleAttribute('hidden', !active);
    }

    const favouritesOn = state.settings.favoritesOnly;
    const favouritesLabel = translate(lang, 'stations.favoritesOnly');
    favourites.setAttribute('aria-label', favouritesLabel);
    favourites.title = favouritesLabel;
    favourites.classList.toggle('active', favouritesOn);
    favourites.setAttribute('aria-pressed', String(favouritesOn));

    emptyAction.textContent = translate(lang, 'stations.add');

    const stations = visibleStations(state.stations, {
      sortMode: state.settings.sortMode,
      favoritesOnly: state.settings.favoritesOnly,
      query: state.query,
      lang,
    });

    const ids = stations.map((s) => s.id).join(' ');
    if (ids !== renderedIds) {
      renderedIds = ids;
      rebuild(stations);
    }

    // Dokler shramba ne odgovori, seznam ni prazen — le še ni znan.
    const showEmpty = state.libraryReady && stations.length === 0;
    empty.toggleAttribute('hidden', !showEmpty);
    list.toggleAttribute('hidden', showEmpty);

    // Prazno zaradi filtra je nekaj drugega kot prazna knjižnica: pot ven
    // je drugačna in »dodajte postajo« bi tu zavajalo.
    const filtered = state.stations.length > 0;
    emptyTitle.textContent = translate(
      lang,
      filtered ? 'stations.empty.filtered.title' : 'stations.empty.title',
    );
    emptyBody.textContent = translate(
      lang,
      filtered ? 'stations.empty.filtered.body' : 'stations.empty.body',
    );
    emptyAction.toggleAttribute('hidden', filtered);

    // Premik gor/dol je smiseln le v vrstnem redu po meri; pri A→Ž bi ga
    // razvrstitev takoj prekrila in izgledalo bi, kot da ni deloval.
    const canReorder = state.settings.sortMode === 'custom';

    for (const [index, row] of rows.entries()) {
      /**
       * Postaja se bere iz **svežega seznama**, ne iz `row.station`.
       *
       * Knjižnica ob vsaki spremembi vrne nove objekte; vrstica pa je bila
       * zgrajena s starim. Branje iz vrstice bi pomenilo, da se ★ obarva
       * šele, ko se seznam preuredi — torej naključno. Poravnava po indeksu
       * drži, ker se vrstice prezidajo, kadar koli se `id`-ji spremenijo.
       */
      const station = stations[index] ?? row.station;
      const active = state.stationId === station.id;
      const playing = active && state.playback === 'playing';

      row.action.textContent = translate(lang, 'station.play', { station: station.name });
      row.title.textContent = station.name;
      // `replaceChildren`, ne `textContent`: mono razpeta mora preživeti.
      row.sub.replaceChildren(formatStationDescription(lang, station), row.subTech);
      row.subTech.textContent = formatStationTech(lang, station);

      // Aktivna postaja nosi brand (--nw-play prek .mint), ne statusne barve.
      row.ico.classList.toggle('mint', active);
      row.face.classList.toggle('on', active);
      if (active) row.main.setAttribute('aria-current', 'true');
      else row.main.removeAttribute('aria-current');

      // V živo nosi piko IN besedo — barvna slepota (CLAUDE.md, pravilo 4).
      row.liveLabel.textContent = translate(lang, 'player.live');
      row.live.toggleAttribute('hidden', !playing);

      // Samo HTTP je dejstvo, ne opozorilo: nevtralna značka z besedo.
      const insecure = isHttpOnly(station);
      row.badge.textContent = translate(lang, 'station.insecure.badge');
      row.badge.title = `${translate(lang, 'station.insecure.title')} ${translate(lang, 'station.insecure.hint')}`;
      row.badge.toggleAttribute('hidden', !insecure);

      // Ime dejanja sledi stanju: gumb pove, kaj bo naredil.
      row.favourite.setAttribute('aria-pressed', String(station.favorite));
      row.favourite.setAttribute(
        'aria-label',
        translate(lang, station.favorite ? 'station.unfavourite' : 'station.favourite', {
          station: station.name,
        }),
      );

      row.kebab.setAttribute(
        'aria-label',
        translate(lang, 'station.more', { station: station.name }),
      );

      row.menuTop.label.textContent = translate(lang, 'station.moveTop');
      row.menuUp.label.textContent = translate(lang, 'station.moveUp');
      row.menuDown.label.textContent = translate(lang, 'station.moveDown');
      row.menuBottom.label.textContent = translate(lang, 'station.moveBottom');
      row.menuShare.label.textContent = translate(lang, 'station.share');
      row.menuShare.button.setAttribute(
        'aria-label',
        translate(lang, 'station.share.aria', { station: row.station.name }),
      );
      row.menuEdit.label.textContent = translate(lang, 'station.edit');
      row.menuDelete.label.textContent = translate(lang, 'station.delete');
      row.swipeEdit.label.textContent = translate(lang, 'station.edit');
      row.swipeDelete.label.textContent = translate(lang, 'station.delete');

      // Onemogočen gumb pove, da dejanje obstaja, a tu ne velja — kar je
      // več, kot pove skrit gumb.
      /**
       * Skok na rob je onemogočen na istem mestu kot korak: na vrhu ni kam
       * gor. Ločena pogoja bi se razšla prvič, ko bi kdo spremenil enega —
       * tiho, ker bi bilo dejanje videti kot vsako drugo.
       */
      const atTop = !canReorder || index === 0;
      const atBottom = !canReorder || index === rows.length - 1;
      row.menuTop.button.disabled = atTop;
      row.menuUp.button.disabled = atTop;
      row.menuDown.button.disabled = atBottom;
      row.menuBottom.button.disabled = atBottom;
    }

    /* ── Način urejanja (P2 §1.7) ──────────────────────────────────── */
    editToggle.textContent = translate(
      lang,
      editing ? 'stations.reorder.done' : 'stations.reorder.start',
    );
    reorderHint.textContent = translate(lang, 'stations.reorder.hint');
    // Gumb skrit, dokler ni vsaj dveh postaj — enega ni kam premakniti.
    // V urejanju ostane viden (nosi »Končaj«), tudi če bi seznam padel na eno.
    editToggle.toggleAttribute('hidden', !editing && state.stations.length < 2);

    // Zgradi `.reorder` seznam le ob PREHODU v urejanje; nato ga upravlja
    // `ds/js/reorder.js` in store spremembe ga ne prezidajo (glej komentar
    // pri `reorderList`).
    if (editing && !wasEditing) buildReorderList(state.stations, lang);
    wasEditing = editing;

    // Urejanje skrije iskanje, ★ in razvrstitev (preurejaš cel seznam, ne
    // filtrirane podmnožice — odločeno 2026-07-23) ter zamenja `.ao-list` z
    // `.reorder`. Prazno stanje v urejanju ne nastopi: gumb je bil skrit.
    searchField.root.toggleAttribute('hidden', editing);
    favourites.toggleAttribute('hidden', editing);
    sortMenu.toggleAttribute('hidden', editing);
    reorderHint.toggleAttribute('hidden', !editing);
    reorderList.toggleAttribute('hidden', !editing);
    if (editing) {
      list.setAttribute('hidden', '');
      empty.setAttribute('hidden', '');
    }
  }

  editToggle.addEventListener('click', () => {
    if (lastState !== null) toggleEdit(lastState);
  });

  return { update: apply };
}
